import { SuccessfullAddMarqueDialog } from '../components/marque/successfullAddMarqueDialog';
import { AddMarqueDialog } from '../components/marque/addMarqueDialog';
import MarquePageHeader from '../components/marque/marquePageHeader';
import MarqueFiltration from '../components/marque/marqueFiltration';
import { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { useHasPermission } from '../hooks/useHasPermission';
import { Divider, Box, LinearProgress } from '@mui/material';
import MarqueList from '../components/marque/marqueList';
import MarqueGrid from '../components/marque/marqueGrid';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';
import '../css/roles/roles.css';

const Marque = () => {

    const hasMarqueCreatePermission = useHasPermission('marques_create');

    const [openAddMarque, setOpenAddMarque] = useState(false);
    const [openSuccessfulAddMarque, setOpenSuccessfulAddMarque] = useState(false);

    const [loading, setLoading] = useState(true);
    const [marques, setMarques] = useState([])
    const [totalMarques, setTotalMarques] = useState('');
    const [filteredMarquesCount, setFilteredMarquesCount] = useState('');
    const [marquesFetched, setMarquesFetched] = useState(0);
    const [selectedDisplayMode, setSelectedDisplayMode] = useState(JSON.parse(localStorage.getItem('selectedBrandsDisplayMode')) || 'grid');
    const [marquesFilters, setMarquesFilters] = useState(
        JSON.parse(localStorage.getItem('marquesFilters')) ||
        { debouncedSearch: '' }
    );
    const defaultSortBy = {
        name: null,
        description: null,
    };
    
    const [sortBy, setSortBy] = useState(() => {
        const storedSortBy = JSON.parse(localStorage.getItem('marqueSortBy')) || {};
        return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
    });

    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'marques',
        pathname: '/dashboard/marques',
        displayMode: selectedDisplayMode,
        itemSizes: { grid: 280, list: 70 },
        resetOnFilters: marquesFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(marquesFetched);

    useEffect(() => {
        setLoading(true)
    }, [marquesFilters]);

    useEffect(() => {
        localStorage.setItem('marquesFilters', JSON.stringify(marquesFilters));
    }, [marquesFilters]);

    useEffect(() => {
        localStorage.setItem('marqueSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem('selectedBrandsDisplayMode', JSON.stringify(selectedDisplayMode));
    }, [selectedDisplayMode]);

    const fetchMarques = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('marque/list', { params: { offset: adjustedOffset, limit, filters: marquesFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedMarques = [];
                // Update the fetched records in their correct positions
                response?.data?.marques?.forEach((marque, index) => { updatedMarques[adjustedOffset + index] = marque });
                // Update the state with the new metiers array
                setMarques(updatedMarques);
                setFilteredMarquesCount(response?.data?.filteredMarquesCount);
                setTotalMarques(response?.data?.totalMarques);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching marques:', error);
            CustomErrorLogger(error, 'fetch marques in marques page');
        }
    }, [marquesFilters, offset, sortBy]);

    useEffect(() => {
        fetchMarques()
    }, [fetchMarques]);


    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Marques" list={true}>
            <MarquePageHeader
                setOpenAddMarque={setOpenAddMarque}
                hasMarqueCreatePermission={hasMarqueCreatePermission}
            />
            <Divider />

            {loading ?
                <Box>
                    <MarqueFiltration
                        filtersEmpty={filtersEmpty}
                        marquesFilters={marquesFilters}
                        setMarquesFilters={setMarquesFilters}
                        totalMarques={totalMarques}
                        marquesFetched={marquesFetched}
                        filteredMarquesCount={filteredMarquesCount}
                        selectedDisplayMode={selectedDisplayMode}
                        setSelectedDisplayMode={setSelectedDisplayMode}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                totalMarques === 0 ?
                    <NoRecord
                        description="Aucun marque ajouté pour le moment"
                        buttonTitle="Ajouter une marque"
                        handleOpenDialog={() => setOpenAddMarque(true)}
                        hasPermission={hasMarqueCreatePermission}
                    /> :
                    <Box>
                        <MarqueFiltration
                            filtersEmpty={filtersEmpty}
                            marquesFilters={marquesFilters}
                            setMarquesFilters={setMarquesFilters}
                            totalMarques={totalMarques}
                            marquesFetched={marquesFetched}
                            filteredMarquesCount={filteredMarquesCount}
                            selectedDisplayMode={selectedDisplayMode}
                            setSelectedDisplayMode={setSelectedDisplayMode}
                        />

                        {selectedDisplayMode === 'grid' ? (
                            <MarqueGrid
                                scrollRef={scrollRef}
                                marques={marques}
                                setOffset={setOffset}
                                filteredMarquesCount={filteredMarquesCount}
                                setMarquesFetched={setMarquesFetched}
                            />
                        ) : (
                            <MarqueList
                                scrollRef={scrollRef}
                                marques={marques}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                setOffset={setOffset}
                                filteredMarquesCount={filteredMarquesCount}
                                setMarquesFetched={setMarquesFetched}
                            />
                        )}

                    </Box>
            }

            {openAddMarque &&
                <AddMarqueDialog
                    setMarques={setMarques}
                    setTotalMarques={setTotalMarques}
                    setFilteredMarquesCount={setFilteredMarquesCount}
                    openAddMarque={openAddMarque}
                    setOpenAddMarque={setOpenAddMarque}
                />
            }

            {openSuccessfulAddMarque &&
                <SuccessfullAddMarqueDialog
                    openSuccessfulAddMarque={openSuccessfulAddMarque}
                    setOpenAddMarque={setOpenAddMarque}
                />
            }

        </Page>
        </ListNavigationProvider>
    );
};

export default Marque;